import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Submit consultation form to Firestore
export const submitConsultationForm = async (formData) => {
  try {
    // Add timestamp and additional metadata
    const submissionData = {
      ...formData,
      submittedAt: serverTimestamp(),
      status: 'new',
      source: 'website'
    };

    // Add document to 'consultations' collection
    const docRef = await addDoc(collection(db, 'consultations'), submissionData);
    
    console.log('Form submitted successfully with ID: ', docRef.id);
    return {
      success: true,
      id: docRef.id,
      message: 'Your consultation request has been submitted successfully!'
    };
  } catch (error) {
    console.error('Error submitting form: ', error);
    
    // Provide more specific error messages
    let errorMessage = 'There was an error submitting your request. Please try again.';
    
    if (error.code === 'permission-denied') {
      errorMessage = 'Firestore security rules are blocking this request. Please check your Firestore rules.';
    } else if (error.code === 'unavailable') {
      errorMessage = 'Firestore service is currently unavailable. Please try again later.';
    } else if (error.code === 'failed-precondition') {
      errorMessage = 'Firestore database is not properly configured. Please enable Firestore in your Firebase project.';
    } else if (error.message.includes('channel')) {
      errorMessage = 'Firestore connection failed. Please ensure Firestore is enabled in your Firebase project.';
    }
    
    return {
      success: false,
      error: error.message,
      code: error.code,
      message: errorMessage
    };
  }
};

// Validate form data
export const validateFormData = (formData) => {
  const errors = {};

  // Required fields validation
  if (!formData.fullName || formData.fullName.trim() === '') {
    errors.fullName = 'Full name is required';
  }


  if (!formData.phone || formData.phone.trim() === '') {
    errors.phone = 'Phone number is required';
  } else if (!isValidPhone(formData.phone, formData.countryCode)) {
    if (formData.countryCode === '+91') {
      errors.phone = 'Please enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9';
    } else {
      errors.phone = 'Please enter a valid phone number';
    }
  }

  if (!formData.goals || formData.goals.length === 0) {
    errors.goals = 'Please select at least one singing goal';
  }

  if (!formData.trainingStartDate || formData.trainingStartDate.trim() === '') {
    errors.trainingStartDate = 'Please select when you plan to start training';
  }

  if (!formData.budget || formData.budget.trim() === '') {
    errors.budget = 'Please select your budget range';
  }

  // Custom expert validation - if "Other" is selected, custom expert name is required
  if (formData.specificExpert === 'Other') {
    if (!formData.customExpert || formData.customExpert.trim() === '') {
      errors.customExpert = 'Please enter the expert name';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Email validation helper
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation helper
const isValidPhone = (phone, countryCode = '+1') => {
  // Remove all non-digit characters except +
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // For India, only allow exactly 10 digits
  if (countryCode === '+91') {
    return /^[6-9]\d{9}$/.test(cleanPhone);
  }
  
  // Basic validation for other countries - should contain only digits and be reasonable length
  const phoneRegex = /^\d{7,15}$/;
  
  if (!phoneRegex.test(cleanPhone)) {
    return false;
  }
  
  // Country-specific validation for other countries
  const countryValidations = {
    '+1': /^\d{10}$/, // US/Canada: 10 digits
    '+44': /^\d{10,11}$/, // UK: 10-11 digits
    '+49': /^\d{10,12}$/, // Germany: 10-12 digits
    '+33': /^\d{9,10}$/, // France: 9-10 digits
    '+86': /^\d{11}$/, // China: 11 digits
    '+81': /^\d{10,11}$/, // Japan: 10-11 digits
    '+55': /^\d{10,11}$/, // Brazil: 10-11 digits
    '+61': /^\d{9,10}$/, // Australia: 9-10 digits
    '+7': /^\d{10,11}$/, // Russia: 10-11 digits
  };
  
  const validation = countryValidations[countryCode];
  return validation ? validation.test(cleanPhone) : phoneRegex.test(cleanPhone);
};
